import { Modal, Tooltip } from '@carbon/react';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ShareKnowledge } from '@carbon/icons-react';

import { useUpdateUserRoleMutation } from 'api/entityEndpoints';
import sharedMessages from 'Components/utils/sharedMessages';
import CommonButton from 'Components/Common/CommonButton/CommonButton';
import { useGetUserPermissionForEntityById } from 'hooks/useGetUserPermission';
import { UserRole } from 'api/dtos/user';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import useGetUser from 'hooks/useGetUser';
import useGetCurrentEntity from 'hooks/useGetCurrentEntity';
import styles from './TransferOwnershipModal.module.scss';

type Props = {
    id: string;
    canTransferOwnership: boolean;
}

const TransferOwnershipModal = ({
    id,
    canTransferOwnership,
}: Props) => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const currentEntity = useGetCurrentEntity();
    const { isOwner } = useGetUserPermissionForEntityById(currentEntityId ?? '');
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const user = useGetUser();
    const isChildEntity = Boolean(currentEntity?.parentId);

    const [update, { isLoading }] = useUpdateUserRoleMutation();

    const updateUserRole = async () => {
        try {
            await update({
                userId: id,
                entityId: currentEntityId ?? '',
                role: {
                    role: UserRole.Owner,
                },
            });
        } catch {
            // already handled by RTK Query
        }
    };

    const onConfirmationModalOpen = () => {
        setConfirmationOpen(true);
    };

    const onConfirmationModalClose = () => {
        setConfirmationOpen(false);
    };

    const onTransferOwnershipSubmit = async () => {
        try {
            await updateUserRole();
            setConfirmationOpen(false);
        } catch {
            // Error handling is managed by RTK Query
        }
    };

    if (isChildEntity) {
        return null;
    }

    const isDisabled = !isOwner || isLoading || !user || id === user.id || !canTransferOwnership;
    const button = (
        <CommonButton
            label={intl.formatMessage(messages.label)}
            kind="ghost"
            disabled={isDisabled}
            onClick={onConfirmationModalOpen}
            icon={<ShareKnowledge size={21} />}
        />
    );

    return (
        <>
            {isDisabled ? (
                <Tooltip
                    label={intl.formatMessage(messages.disabledTooltip)}
                    align="left"
                >
                    <span className={styles.tooltipWrapper}>
                        {button}
                    </span>
                </Tooltip>
            ) : button}
            <Modal
                preventCloseOnClickOutside
                open={confirmationOpen}
                onRequestClose={onConfirmationModalClose}
                onRequestSubmit={onTransferOwnershipSubmit}
                modalHeading={intl.formatMessage(messages.modalHeading)}
                modalLabel={intl.formatMessage(messages.modalLabel)}
                danger
                primaryButtonText={intl.formatMessage(sharedMessages.submit)}
                closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
                secondaryButtonText={intl.formatMessage(sharedMessages.back)}
            >{intl.formatMessage(messages.description)}
            </Modal>
        </>
    );
};

const messages = defineMessages({
    modalHeading: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.TransferOwnershipModal.modalHeading',
        defaultMessage: 'Przenieść własność grupy?',
    },
    modalLabel: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.TransferOwnershipModal.modalLabel',
        defaultMessage: 'Potwierdź przeniesienie własności grupy na innego użytkownika.',
    },
    label: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.TransferOwnershipModal.label',
        defaultMessage: 'Przenieś własność',
    },
    description: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.TransferOwnershipModal.disabledTooltip.description',
        defaultMessage: 'Transfer jest niemożliwy, gdy użytkownik osiągnął maksymalną liczbę grup. W takim wypadku wystąpi błąd.',
    },
    disabledTooltip: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityInteractions.TransferOwnershipModal.disabledTooltip',
        defaultMessage: 'Transfer jest niemożliwy, gdy użytkownik jest już właścicielem tej grupy, nie ma wykupionej odpowiedniej subskrypcji albo osiągnął maksymalną liczbę grup.',
    },
});

export default TransferOwnershipModal;
